import React, { useState } from 'react';
import { Bell, Mail, MessageSquare, Save } from 'lucide-react';

const NotificationsSection = () => {
  const [settings, setSettings] = useState({
    email: {
      projectUpdates: true,
      securityAlerts: true,
      billingNotifications: true,
      marketingEmails: false,
      weeklyDigest: true,
    },
    push: {
      projectUpdates: true,
      securityAlerts: true,
      billingNotifications: false,
      teamInvitations: true,
    },
    inApp: {
      projectUpdates: true,
      securityAlerts: true,
      teamActivity: true,
      systemMaintenance: true,
    },
  });

  const handleToggle = (category: keyof typeof settings, setting: string) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: !prev[category][setting as keyof typeof prev[typeof category]],
      },
    }));
  };

  const handleSave = () => {
    // TODO: Implement actual save functionality
    console.log('Saving notification settings:', settings);
    alert('Notification settings saved!');
  };

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <button
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-brand-primary' : 'bg-ui-border-primary'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  const sections = [
    {
      title: 'Email Notifications',
      description: 'Receive notifications via email',
      icon: Mail,
      category: 'email',
      settings: [
        { key: 'projectUpdates', label: 'Project Updates', description: 'Get notified about project changes' },
        { key: 'securityAlerts', label: 'Security Alerts', description: 'Important security notifications' },
        { key: 'billingNotifications', label: 'Billing Notifications', description: 'Payment and billing updates' },
        { key: 'marketingEmails', label: 'Marketing Emails', description: 'Product updates and newsletters' },
        { key: 'weeklyDigest', label: 'Weekly Digest', description: 'Summary of your weekly activity' },
      ],
    },
    {
      title: 'Push Notifications',
      description: 'Receive push notifications on your devices',
      icon: Bell,
      category: 'push',
      settings: [
        { key: 'projectUpdates', label: 'Project Updates', description: 'Get notified about project changes' },
        { key: 'securityAlerts', label: 'Security Alerts', description: 'Important security notifications' },
        { key: 'billingNotifications', label: 'Billing Notifications', description: 'Payment and billing updates' },
        { key: 'teamInvitations', label: 'Team Invitations', description: 'New team member invitations' },
      ],
    },
    {
      title: 'In-App Notifications',
      description: 'Receive notifications within the application',
      icon: MessageSquare,
      category: 'inApp',
      settings: [
        { key: 'projectUpdates', label: 'Project Updates', description: 'Get notified about project changes' },
        { key: 'securityAlerts', label: 'Security Alerts', description: 'Important security notifications' },
        { key: 'teamActivity', label: 'Team Activity', description: 'Updates from team members' },
        { key: 'systemMaintenance', label: 'System Maintenance', description: 'Planned maintenance notifications' },
      ],
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">Notification Preferences</h2>
        <p className="text-neutral-600 dark:text-neutral-400">Choose how and when you want to be notified.</p>
      </div>

      <div className="space-y-8">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <div key={section.category} className="bg-ui-background-secondary rounded-lg border border-ui-border-primary p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                  <Icon size={20} className="text-brand-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-ui-text-primary">{section.title}</h3>
                  <p className="text-sm text-ui-text-secondary">{section.description}</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {section.settings.map((setting) => (
                  <div key={setting.key} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-ui-text-primary">{setting.label}</div>
                      <div className="text-sm text-ui-text-secondary">{setting.description}</div>
                    </div>
                    <Toggle
                      checked={settings[section.category as keyof typeof settings][setting.key as never]}
                      onChange={() => handleToggle(section.category as keyof typeof settings, setting.key)}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Save Button */}
        <div className="pt-6 border-t border-ui-border-secondary">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-6 py-3 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors"
            data-tour="save-notifications-button"
          >
            <Save size={16} />
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsSection; 